import { BarChart3, CheckCircle2 } from "lucide-react";
import { formatCurrency } from "../../../../format";
import { calculateDataConfidence, describeComparableDifferences, describeComparableUnknowns } from "../../calculations/similarity";
import type { MarketAnalysisComparable, MarketFunnelSnapshot, MarketSubjectSnapshot, MarketValuationResult } from "../../domain/types";
import { comparableDisplayIdentity } from "../../domain/listingIdentity";
import { effectiveApartmentArea, isCheaperBetterConditionAlternative } from "../../domain/propertyMatching";
import { buildPricePosition } from "../../calculations/pricePosition";
import { activeMarketComparables, activeMarketCompetitors } from "../../domain/comparableLifecycle";
import { MarketFunnelContext } from "../MarketFunnelContext";

const confidenceLabel = { low: "Nízka", medium: "Stredná", high: "Vysoká" } as const;
const positionLabel = {
  below: "Pod trhovým pásmom",
  within: "V trhovom pásme",
  above: "Nad trhovým pásmom",
  unknown: "Bez cenovej pozície"
} as const;

type ResultStepProps = {
  subject: MarketSubjectSnapshot;
  comparables: MarketAnalysisComparable[];
  marketFunnelSnapshot?: MarketFunnelSnapshot;
  result?: MarketValuationResult;
};

export function ResultStep({ subject, comparables, marketFunnelSnapshot, result }: ResultStepProps) {
  if (!result) return <div className="market-error">Výsledok ešte nebol vypočítaný.</div>;
  const range = Math.max(1, result.marketHigh - result.marketLow);
  const ownerPosition = subject.askingPriceAtAnalysis
    ? Math.max(0, Math.min(100, ((subject.askingPriceAtAnalysis - result.marketLow) / range) * 100))
    : 50;
  const relevant = activeMarketComparables(comparables)
    .filter((comparable) => comparable.decision !== "excluded")
    .sort((first, second) => second.scoreBreakdown.selectionScore - first.scoreBreakdown.selectionScore);
  const competitors = activeMarketCompetitors(comparables);
  const pricePosition = buildPricePosition(subject, relevant, competitors);
  const marketThreats = competitors.filter((comparable) => isCheaperBetterConditionAlternative(subject, comparable));
  const competitionCountLabel = competitors.length === 1
    ? "1 ďalšia relevantná ponuka"
    : competitors.length >= 2 && competitors.length <= 4
      ? `${competitors.length} ďalšie relevantné ponuky`
      : `${competitors.length} ďalších relevantných ponúk`;

  return (
    <div className="market-step-content">
      <div className="market-step-intro">
        <p className="market-kicker">Výsledok analýzy</p>
        <h2>Cenová pozícia nehnuteľnosti</h2>
        <p>Trhové pásmo, cenová pozícia a odporúčaná inzertná cena na jednom mieste.</p>
      </div>

      <MarketFunnelContext snapshot={marketFunnelSnapshot} resultMode />

      <div className="market-result-hero">
        <div><span>PREDSTAVA MAJITEĽA</span><strong>{subject.askingPriceAtAnalysis ? formatCurrency(subject.askingPriceAtAnalysis) : "Neuvedené"}</strong></div>
        <div><span>TRHOVÉ PÁSMO</span><strong>{formatCurrency(result.marketLow)} – {formatCurrency(result.marketHigh)}</strong></div>
        <div className="recommended"><span>ODPORÚČANÁ INZERTNÁ CENA</span><strong>{formatCurrency(result.recommendedListingPrice)}</strong></div>
      </div>

      <section className="market-position-card">
        <header><div><span>Pozícia voči trhu</span><strong>{positionLabel[result.ownerPricePosition]}</strong></div><BarChart3 size={20} /></header>
        <div className="market-range-track">
          <span className="market-owner-marker" style={{ left: `${ownerPosition}%` }} />
        </div>
        <div className="market-range-labels"><span>{formatCurrency(result.marketLow)}</span><span>{formatCurrency(result.marketHigh)}</span></div>
        {result.ownerDifferencePercent !== undefined ? (
          <p>
            {result.ownerDifferenceAmount !== undefined ? `${result.ownerDifferenceAmount > 0 ? "+" : "−"}${formatCurrency(Math.abs(result.ownerDifferenceAmount))} · ` : ""}
            {Math.abs(result.ownerDifferencePercent)} % {result.ownerPricePosition === "above" ? "nad hornou hranicou trhu" : result.ownerPricePosition === "below" ? "pod trhovým pásmom" : "v rámci trhového pásma"}.
          </p>
        ) : null}
      </section>

      <div className="market-dataset-grid">
        <MarketDataset title="Ponukový trh" data={result.askingMarket} />
        <MarketDataset title="Realizovaný trh" data={result.realizedMarket} />
      </div>

      <div className="market-confidence"><CheckCircle2 size={18} /><span>Istota výsledku</span><strong>{result.confidenceScore}/100 · {confidenceLabel[result.confidence]}</strong></div>
      {relevant.length < 3 ? (
        <div className="market-fallback-note" role="status">
          <strong>Nižšia istota odhadu</strong>
          <span>Pre presné ocenenie máme iba {relevant.length} {relevant.length === 1 ? "kvalitné porovnanie" : "kvalitné porovnania"}.</span>
        </div>
      ) : null}
      <div className="market-context-insight">
        <strong>Konkurencia, ktorú vidí kupujúci</strong>
        <span>{competitors.length === 0
          ? "V aktuálnom výbere nie je ďalšia užitočná trhová alternatíva."
          : `Na trhu ${competitors.length === 1 ? "je" : "sú"} zároveň ${competitionCountLabel}, ktoré kupujúci porovnáva s naším bytom.`}</span>
        {marketThreats.length > 0 ? <b>Lacnejšia alternatíva v lepšom stave: {marketThreats.length}</b> : null}
      </div>

      {pricePosition ? (
        <section className="market-price-position">
          <div className="market-section-title"><span>CENOVÉ PORADIE</span><small>ponukové ceny · nízka → vysoká</small></div>
          <div className="market-price-position-summary">
            <span>Pozícia<strong>{pricePosition.subjectPosition ? `${pricePosition.subjectPosition}. z ${pricePosition.entries.length}` : "Bez ceny subjectu"}</strong></span>
            <span>Medián<strong>{formatCurrency(pricePosition.median)}</strong></span>
            <span>Rozdiel<strong>{pricePosition.differenceFromMedian !== undefined ? `${pricePosition.differenceFromMedian >= 0 ? "+" : "−"}${formatCurrency(Math.abs(pricePosition.differenceFromMedian))}` : "–"}</strong></span>
            <span>Rozdiel %<strong>{pricePosition.differenceFromMedianPercent !== undefined ? `${pricePosition.differenceFromMedianPercent >= 0 ? "+" : ""}${pricePosition.differenceFromMedianPercent} %` : "–"}</strong></span>
            <span>Najnižšia<strong>{formatCurrency(pricePosition.lowest)}</strong></span>
            <span>Najvyššia<strong>{formatCurrency(pricePosition.highest)}</strong></span>
          </div>
          <ol className="market-price-ladder">
            {pricePosition.entries.map((entry) => (
              <li className={entry.kind} key={entry.id}>
                <span>{entry.position}</span><strong>{entry.subject ? "NÁŠ BYT" : entry.label}<small>{entry.kind === "primary" ? "Porovnateľný byt" : entry.kind === "competition" ? "Konkurencia" : "Naša ponuka"}</small></strong><b>{formatCurrency(entry.askingPrice)}</b>
              </li>
            ))}
          </ol>
          {pricePosition.insight ? <p className="market-price-insight">{pricePosition.insight}</p> : null}
        </section>
      ) : null}

      <section className="market-relevant-section">
        <div className="market-section-title"><span>KVALITNÉ POROVNANIA: {relevant.length}</span><small>použité na ocenenie</small></div>
        {relevant.map((comparable) => {
          const differences = describeComparableDifferences(subject, comparable);
          const unknowns = describeComparableUnknowns(subject, comparable);
          const dataConfidence = calculateDataConfidence(subject, comparable);
          const identity = comparableDisplayIdentity(comparable);
          const comparableArea = effectiveApartmentArea(comparable).value;
          return (
            <article className="market-relevant-row" key={comparable.id}>
              <strong>{Math.round(comparable.similarityScore)} %</strong>
              <div>
                <h3>{identity.title}</h3>
                <p>{differences.length > 0 ? differences.join(" · ") : "Žiadny potvrdený rozdiel"}</p>
                <small>Dôvera {confidenceLabel[dataConfidence.level]} · {dataConfidence.score} %{unknowns.length > 0 ? ` · Neznáme: ${unknowns.join(", ")}` : ""}</small>
              </div>
              <div className="market-relevant-prices">
                {comparable.askingPrice ? <span className="asking">PONUKA {formatCurrency(comparable.askingPrice)} · {comparableArea ? Math.round(comparable.askingPrice / comparableArea).toLocaleString("sk-SK") : "–"} €/m²</span> : null}
                {comparable.realizedPrice ? <span className="realized">REALIZÁCIA {formatCurrency(comparable.realizedPrice)} · {comparableArea ? Math.round(comparable.realizedPrice / comparableArea).toLocaleString("sk-SK") : "–"} €/m²</span> : null}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}

function MarketDataset({ title, data }: { title: string; data: MarketValuationResult["askingMarket"] }) {
  return (
    <section className="market-dataset-card">
      <span>{title}</span>
      {data ? <><strong>{data.count} zdrojov</strong><p>Medián {data.medianPricePerM2.toLocaleString("sk-SK")} €/m²</p><p>Vážený odhad {data.weightedPricePerM2.toLocaleString("sk-SK")} €/m²</p></> : <p>Bez dostupných dát</p>}
    </section>
  );
}
